<template>
  <div>
    <!-- Breadcrumbs -->
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-3">
        <li class="breadcrumb-item">
          <router-link to="/">Home</router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link to="/registered-users">Registered Users</router-link>
        </li>
        <li class="breadcrumb-item active" aria-current="page">
          User Workspaces
        </li>
      </ol>
    </nav>

    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div
              class="card-header d-flex justify-content-between align-items-center"
            >
              <div>
                <h5 class="mb-1">User Workspaces</h5>
                <small class="text-muted" v-if="userInfo">
                  Showing workspaces for user:
                  <strong>{{ userInfo.username }}</strong>
                </small>
              </div>
              <router-link
                to="/registered-users"
                class="btn btn-outline-secondary btn-sm"
              >
                <i class="fa fa-arrow-left me-1"></i>
                Back to Users
              </router-link>
            </div>

            <div class="card-body">
              <!-- Loading State -->
              <div v-if="isLoading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <p class="mt-2">Loading user workspaces...</p>
              </div>

              <!-- Error State -->
              <div v-else-if="error" class="alert alert-danger">
                <div class="d-flex align-items-center">
                  <i class="fa fa-exclamation-triangle me-2"></i>
                  <div class="flex-grow-1">
                    <strong>Error:</strong> {{ error }}
                  </div>
                  <button
                    class="btn btn-sm btn-outline-danger"
                    @click="loadWorkspaces"
                  >
                    <i class="fa fa-refresh"></i> Retry
                  </button>
                </div>
              </div>

              <!-- Workspaces Grid -->
              <div v-else-if="workspaces.length > 0" class="row">
                <div
                  v-for="workspace in workspaces"
                  :key="workspace._id"
                  class="col-lg-4 col-md-6 col-sm-12 mb-4"
                >
                  <div class="card h-100 workspace-card border-0 shadow-sm">
                    <!-- Workspace Logo -->
                    <div class="card-img-top workspace-logo-container">
                      <img
                        v-if="workspace.logo"
                        :src="workspace.logo"
                        :alt="workspace.title"
                        class="workspace-logo"
                      />
                      <div
                        v-else
                        class="workspace-logo-placeholder d-flex align-items-center justify-content-center"
                      >
                        <i class="fa fa-briefcase fa-2x text-muted"></i>
                      </div>
                    </div>

                    <div class="card-body d-flex flex-column">
                      <!-- Workspace Title -->
                      <h6
                        class="card-title text-truncate"
                        :title="workspace.title"
                      >
                        {{ workspace.title }}
                        <span
                          v-if="workspace.is_ai_generated"
                          class="badge bg-primary ms-2"
                          title="AI Generated Workspace"
                        >
                          <i class="fa fa-robot me-1"></i>AI
                        </span>
                      </h6>

                      <!-- Workspace ID -->
                      <div class="mb-2">
                        <small class="text-muted">ID:</small>
                        <code class="bg-light px-1 rounded">{{
                          workspace._id.slice(-8)
                        }}</code>
                      </div>

                      <!-- Workspace Idea/Description -->
                      <p class="card-text text-muted small flex-grow-1">
                        <span v-if="workspace.idea && workspace.idea.trim()">
                          {{ workspace.idea }}
                        </span>
                        <span v-else class="fst-italic"
                          >No description provided</span
                        >
                      </p>

                      <!-- Workspace Stats -->
                      <div class="mt-auto">
                        <div class="row g-2 text-center">
                          <div class="col-4">
                            <div class="d-flex flex-column">
                              <span class="h6 mb-0 text-primary">{{
                                workspace.no_of_lanes
                              }}</span>
                              <small class="text-muted">Lanes</small>
                            </div>
                          </div>
                          <div class="col-4">
                            <div class="d-flex flex-column">
                              <span class="h6 mb-0 text-success">{{
                                workspace.no_of_modules
                              }}</span>
                              <small class="text-muted">Modules</small>
                            </div>
                          </div>
                          <div class="col-4">
                            <div class="d-flex flex-column">
                              <span class="h6 mb-0 text-info">{{
                                workspace.no_of_people
                              }}</span>
                              <small class="text-muted">People</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Workspace Footer with Actions -->
                    <div class="card-footer bg-transparent border-top-0 pt-0">
                      <div
                        class="d-flex justify-content-between align-items-center"
                      >
                        <div class="text-muted">
                          <small>
                            <i class="fa fa-calendar me-1"></i>
                            Created {{ formatTimeAgo(workspace._id) }}
                          </small>
                        </div>
                        <div>
                          <button
                            class="btn btn-outline-primary btn-sm"
                            @click="viewWorkspaceDetails(workspace)"
                          >
                            <i class="fa fa-eye me-1"></i>
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-5">
                <div class="mb-3">
                  <i class="fa fa-briefcase fa-3x text-muted"></i>
                </div>
                <h5 class="text-muted">No Workspaces Found</h5>
                <p class="text-muted">
                  This user hasn't created any workspaces yet.
                </p>
                <div class="mt-3">
                  <button class="btn btn-primary me-2" @click="loadWorkspaces">
                    <i class="fa fa-refresh me-1"></i>
                    Refresh
                  </button>
                  <router-link
                    to="/registered-users"
                    class="btn btn-outline-secondary"
                  >
                    <i class="fa fa-arrow-left me-1"></i>
                    Back to Users
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Summary Stats Footer -->
            <div v-if="workspaces.length > 0" class="card-footer bg-light">
              <div class="row text-center">
                <div class="col-md-3 col-6 mb-2 mb-md-0">
                  <div class="d-flex flex-column">
                    <span class="h5 mb-0 text-primary">{{
                      workspaces.length
                    }}</span>
                    <small class="text-muted">Total Workspaces</small>
                  </div>
                </div>
                <div class="col-md-3 col-6 mb-2 mb-md-0">
                  <div class="d-flex flex-column">
                    <span class="h5 mb-0 text-success">{{ totalLanes }}</span>
                    <small class="text-muted">Total Lanes</small>
                  </div>
                </div>
                <div class="col-md-3 col-6 mb-2 mb-md-0">
                  <div class="d-flex flex-column">
                    <span class="h5 mb-0 text-warning">{{ totalModules }}</span>
                    <small class="text-muted">Total Modules</small>
                  </div>
                </div>
                <div class="col-md-3 col-6">
                  <div class="d-flex flex-column">
                    <span class="h5 mb-0 text-info">{{ totalPeople }}</span>
                    <small class="text-muted">Total People</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Workspace Details Modal -->
    <div
      v-if="showModal && selectedWorkspace"
      class="modal fade show d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Workspace Details: {{ selectedWorkspace.title }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-4">
                <img
                  v-if="selectedWorkspace.logo"
                  :src="selectedWorkspace.logo"
                  :alt="selectedWorkspace.title"
                  class="img-fluid rounded"
                />
                <div
                  v-else
                  class="bg-light rounded d-flex align-items-center justify-content-center"
                  style="height: 200px"
                >
                  <i class="fa fa-briefcase fa-3x text-muted"></i>
                </div>
              </div>
              <div class="col-md-8">
                <h6>Basic Information</h6>
                <table class="table table-borderless table-sm">
                  <tbody>
                    <tr>
                      <td><strong>ID:</strong></td>
                      <td>
                        <code>{{ selectedWorkspace._id }}</code>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Title:</strong></td>
                      <td>{{ selectedWorkspace.title }}</td>
                    </tr>
                    <tr>
                      <td><strong>Idea:</strong></td>
                      <td>
                        {{
                          selectedWorkspace.idea || "No description provided"
                        }}
                      </td>
                    </tr>
                    <tr>
                      <td><strong>AI Generated:</strong></td>
                      <td>
                        <span
                          :class="
                            selectedWorkspace.is_ai_generated
                              ? 'badge bg-success'
                              : 'badge bg-secondary'
                          "
                        >
                          <i
                            :class="
                              selectedWorkspace.is_ai_generated
                                ? 'fa fa-robot'
                                : 'fa fa-user'
                            "
                            class="me-1"
                          ></i>
                          {{
                            selectedWorkspace.is_ai_generated
                              ? "Yes (AI Generated)"
                              : "No (Manual)"
                          }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <h6>Statistics</h6>
                <table class="table table-borderless table-sm">
                  <tbody>
                    <tr>
                      <td><strong>Lanes:</strong></td>
                      <td>
                        <span class="badge bg-primary">{{
                          selectedWorkspace.no_of_lanes
                        }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Modules:</strong></td>
                      <td>
                        <span class="badge bg-success">{{
                          selectedWorkspace.no_of_modules
                        }}</span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>People:</strong></td>
                      <td>
                        <span class="badge bg-info">{{
                          selectedWorkspace.no_of_people
                        }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import apiService from "@/services/apiService";

// Types
interface Workspace {
  _id: string;
  logo: string | null;
  title: string;
  idea: string;
  no_of_lanes: number;
  no_of_modules: number;
  no_of_people: number;
  is_ai_generated: boolean;
}

interface UserInfo {
  _id: string;
  username: string;
  logo: string | null;
}

// Route params
const route = useRoute();
const userId = computed(() => route.params.userId as string);

// Reactive data
const workspaces = ref<Workspace[]>([]);
const userInfo = ref<UserInfo | null>(null);
const selectedWorkspace = ref<Workspace | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);
const showModal = ref(false);

// Computed properties
const totalLanes = computed(() =>
  workspaces.value.reduce((sum, ws) => sum + ws.no_of_lanes, 0)
);

const totalModules = computed(() =>
  workspaces.value.reduce((sum, ws) => sum + ws.no_of_modules, 0)
);

const totalPeople = computed(() =>
  workspaces.value.reduce((sum, ws) => sum + ws.no_of_people, 0)
);

// Methods
const loadWorkspaces = async () => {
  if (!userId.value) {
    error.value = "No user ID provided";
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    console.log(`Loading workspaces for user: ${userId.value}`);
    const response = await apiService.get(
      `/common/cms/users/${userId.value}/workspaces`
    );

    console.log("API Response:", response);

    // The apiService.get() already extracts the data from the standardized response
    if (Array.isArray(response)) {
      workspaces.value = response;
      console.log(`Loaded ${workspaces.value.length} workspaces`);

      // Also try to get user info for display
      await loadUserInfo();
    } else {
      console.error("Invalid response structure:", response);
      throw new Error(
        `Invalid response format. Expected array, got: ${typeof response}`
      );
    }
  } catch (err: any) {
    console.error("Error loading workspaces:", err);
    error.value =
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load workspaces";
  } finally {
    isLoading.value = false;
  }
};

const loadUserInfo = async () => {
  try {
    // Try to get user info from the users list
    const response = await apiService.get("/common/cms/users");

    // The apiService.get() already extracts the data from the standardized response
    if (Array.isArray(response)) {
      const user = response.find((u: any) => u._id === userId.value);
      if (user) {
        userInfo.value = {
          _id: user._id,
          username: user.username,
          logo: user.logo,
        };
      }
    }
  } catch (err) {
    console.warn("Could not load user info:", err);
    // Non-critical error, continue without user info
  }
};

const viewWorkspaceDetails = (workspace: Workspace) => {
  selectedWorkspace.value = workspace;
  showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedWorkspace.value = null;
};

const formatTimeAgo = (objectId: string): string => {
  // Extract timestamp from MongoDB ObjectId (first 8 hex chars = 4 bytes = Unix timestamp)
  const timestamp = parseInt(objectId.substring(0, 8), 16) * 1000;
  const date = new Date(timestamp);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) {
    return "today";
  } else if (diffInDays === 1) {
    return "yesterday";
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return `${months} month${months > 1 ? "s" : ""} ago`;
  } else {
    const years = Math.floor(diffInDays / 365);
    return `${years} year${years > 1 ? "s" : ""} ago`;
  }
};

// Lifecycle
onMounted(() => {
  loadWorkspaces();
});
</script>

<style scoped>
.workspace-card {
  transition: all 0.3s ease;
  cursor: default;
}

.workspace-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1) !important;
}

.workspace-logo-container {
  height: 180px;
  overflow: hidden;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.workspace-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.workspace-logo-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.card-title {
  font-weight: 600;
  color: #2c3e50;
}

.breadcrumb-item a {
  text-decoration: none;
  color: #6c757d;
}

.breadcrumb-item a:hover {
  color: #495057;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
}

code {
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .workspace-logo-container {
    height: 150px;
  }

  .card-footer .col-6 {
    margin-bottom: 0.5rem;
  }
}

.table-borderless td {
  padding: 0.25rem 0.5rem;
}

.modal-body .img-fluid {
  max-height: 250px;
  width: 100%;
  object-fit: cover;
}
</style>
